export const KNOWN_PAGE_ROUTES = {
  1: '/Udesh',
  2: '/Contact',
  3: '/Cometee',
}

export function pagePath(page) {
  if (!page) return '/'
  const custom = String(page.page_url || '').trim().replace(/^\/+/, '')
  if (custom) return `/${custom}`
  const id = Number(page.page_id)
  if (KNOWN_PAGE_ROUTES[id]) return KNOWN_PAGE_ROUTES[id]
  return `/page/${id}`
}

export function sortMenuPages(pages) {
  return [...(pages || [])].sort((a, b) => {
    const seqA = Number(a.menu_sequence) || 0
    const seqB = Number(b.menu_sequence) || 0
    if (seqA !== seqB) return seqA - seqB
    return (Number(a.page_id) || 0) - (Number(b.page_id) || 0)
  })
}

/** Published CMS pages are public by default; Inactive is the hide flag. */
export function isPageActive(page) {
  if (!page) return false
  const status = page.status
  if (status == null || status === '') return true
  if (Number(status) === 1) return true
  if (Number(status) === 0) return false
  const normalized = String(status).trim().toLowerCase()
  if (normalized === 'active') return true
  if (normalized === 'inactive') return false
  return true
}

export function pagesToMenuItems(pages) {
  return sortMenuPages((pages || []).filter(isPageActive)).map((page) => ({
    label: page.title,
    url: pagePath(page),
    open_new_tab: 0,
  }))
}

function normalizeMenuUrlKey(url) {
  const href = menuHref(url)
  if (isExternalUrl(href)) return href.replace(/\/$/, '').toLowerCase()
  const path = href.replace(/\/$/, '') || '/'
  return path.toLowerCase()
}

/** Hardcoded header links used when menu API has no active items. */
export const DEFAULT_HEADER_MENU = [
  { label: 'मुख्य पृष्ठ', url: '/' },
  { label: 'ऑनलाइन दान', url: '/Donation' },
  { label: 'गैलरी', url: '/Gallary' },
]

export function isExternalUrl(url) {
  return /^https?:\/\//i.test(String(url || '').trim())
}

/** Normalize stored menu URL for React Router Link (internal paths). */
export function menuHref(url) {
  const raw = String(url || '').trim()
  if (!raw || raw === '#') return '#'
  if (isExternalUrl(raw)) return raw
  if (raw.startsWith('/')) return raw
  return `/${raw}`
}

function parentKey(item) {
  const pid = item?.parent_id
  if (pid == null || pid === '' || Number(pid) === 0) return null
  return Number(pid)
}

/**
 * Build one-level menu tree from a flat menu_items list.
 * Top-level items first (by sort_order); children nested under `children`.
 */
export function buildMenuTree(items) {
  const list = Array.isArray(items) ? [...items] : []
  const byParent = new Map()
  const roots = []

  for (const item of list) {
    const pid = parentKey(item)
    if (pid == null) {
      roots.push({ ...item, children: [] })
      continue
    }
    if (!byParent.has(pid)) byParent.set(pid, [])
    byParent.get(pid).push({ ...item, children: [] })
  }

  const sortFn = (a, b) =>
    (Number(a.sort_order) || 0) - (Number(b.sort_order) || 0) ||
    (Number(a.menu_item_id) || 0) - (Number(b.menu_item_id) || 0)

  roots.sort(sortFn)

  return roots.map((root) => {
    const kids = byParent.get(Number(root.menu_item_id)) || []
    kids.sort(sortFn)
    return { ...root, children: kids }
  })
}

/**
 * Keep configured menu_items first; append Active CMS pages that are not
 * already linked (by URL or label). Flat list in → flat list out.
 */
export function mergeMenuWithCmsPages(menuItems, pages) {
  const base = Array.isArray(menuItems) ? [...menuItems] : []
  const seenUrls = new Set(base.map((item) => normalizeMenuUrlKey(item.url)))
  const seenLabels = new Set(
    base.map((item) => String(item.label || '').trim().toLowerCase()).filter(Boolean)
  )
  let nextOrder =
    base.reduce((max, item) => Math.max(max, Number(item.sort_order) || 0), 0) + 1

  for (const item of pagesToMenuItems(pages)) {
    const urlKey = normalizeMenuUrlKey(item.url)
    const labelKey = String(item.label || '').trim().toLowerCase()
    if (seenUrls.has(urlKey) || (labelKey && seenLabels.has(labelKey))) continue
    base.push({ ...item, parent_id: null, sort_order: nextOrder++ })
    seenUrls.add(urlKey)
    if (labelKey) seenLabels.add(labelKey)
  }
  return base
}
